import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ChatButton from './ChatButton';
import SimpleChatWindow from './SimpleChatWindow';
import ThankYouScreen from './ThankYouScreen';
import { simpleChatApi, type SimpleChatButton, type SimpleChatStage } from '@/lib/api';
import { useChatSse, type AdminChatMessage } from '@/hooks/useChatSse';

interface SimpleChatState {
    sessionId: string | null;
    isOpen: boolean;
    stage: SimpleChatStage;
    messages: Array<{
        id: string;
        role: 'user' | 'assistant';
        content: string;
        timestamp: Date;
    }>;
    buttons: SimpleChatButton[];
    expectingText: boolean;
    inputPlaceholder: string;
    isLoading: boolean;
    selectedFeatures: string[];
}

const SimpleChatWidget = () => {
    const { t } = useTranslation();
    const [state, setState] = useState<SimpleChatState>({
        sessionId: null,
        isOpen: false,
        stage: 'MAIN_MENU',
        messages: [],
        buttons: [],
        expectingText: false,
        inputPlaceholder: '',
        isLoading: false,
        selectedFeatures: [],
    });

    // Restore session from localStorage on mount
    useEffect(() => {
        const savedSession = localStorage.getItem('simple_chat_session');
        if (savedSession) {
            // Restore session and fetch current state
            restoreSession(savedSession);
        }

        // Listen for open-chat event
        const handleOpenChat = () => {
            setState(prev => {
                if (!prev.isOpen) {
                    // Triggers the session start check in the next effect or we call it manually here?
                    // Since we can't easily call async startNewSession inside setState, we'll just open it.
                    // But we need to ensure session starts.
                    // Let's use a separate logic or just trigger the toggle.
                    return { ...prev, isOpen: true };
                }
                return prev;
            });
        };

        window.addEventListener('open-chat', handleOpenChat);
        return () => window.removeEventListener('open-chat', handleOpenChat);
    }, []);

    // Effect to start session when opened if none exists
    useEffect(() => {
        if (state.isOpen && !state.sessionId && !state.isLoading) {
            startNewSession();
        }
    }, [state.isOpen, state.sessionId]);

    const handleAdminMessage = useCallback((message: AdminChatMessage) => {
        setState((prev) => ({
            ...prev,
            messages: [
                ...prev.messages,
                {
                    id: message.id,
                    role: 'assistant',
                    content: message.content,
                    timestamp: new Date(message.createdAt),
                },
            ],
        }));
    }, []);

    useChatSse({
        sessionId: state.sessionId,
        onAdminMessage: handleAdminMessage,
        enabled: !!state.sessionId,
    });

    const restoreSession = async (sessionId: string) => {
        try {
            const response = await simpleChatApi.getSession(sessionId);
            if (response.data.success) {
                const session = response.data.data;
                setState(prev => ({
                    ...prev,
                    sessionId: session.sessionId,
                    stage: session.stage,
                    selectedFeatures: session.collectedData.features || [],
                }));
                // Re-fetch current stage to get buttons
                const stageResponse = await simpleChatApi.action(sessionId, 'refresh');
                if (stageResponse.data.success) {
                    updateFromResponse(stageResponse.data.data);
                }
            }
        } catch {
            // Session expired or invalid, start fresh
            localStorage.removeItem('simple_chat_session');
        }
    };

    const toggleWidget = async () => {
        setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
    };

    const startNewSession = async () => {
        setState(prev => ({ ...prev, isLoading: true }));

        try {
            const response = await simpleChatApi.start();

            if (response.data.success) {
                const data = response.data.data;

                // Save session to localStorage
                localStorage.setItem('simple_chat_session', data.sessionId);

                setState(prev => ({
                    ...prev,
                    sessionId: data.sessionId,
                    stage: data.stage,
                    messages: [{
                        id: '1',
                        role: 'assistant',
                        content: data.message,
                        timestamp: new Date(),
                    }],
                    buttons: data.buttons,
                    expectingText: data.expectingText || false,
                    inputPlaceholder: data.inputPlaceholder || '',
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error('Failed to start simple chat:', error);
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const updateFromResponse = (data: {
        sessionId: string;
        stage: SimpleChatStage;
        message: string;
        buttons: SimpleChatButton[];
        expectingText?: boolean;
        inputPlaceholder?: string;
    }) => {
        const assistantMessage = {
            id: Date.now().toString(),
            role: 'assistant' as const,
            content: data.message,
            timestamp: new Date(),
        };

        setState(prev => ({
            ...prev,
            stage: data.stage,
            messages: [...prev.messages, assistantMessage],
            buttons: data.buttons || [],
            expectingText: data.expectingText || false,
            inputPlaceholder: data.inputPlaceholder || '',
            isLoading: false,
            // Update selected features from button states
            selectedFeatures: (data.buttons || [])
                .filter(btn => btn.selected)
                .map(btn => btn.action),
        }));
    };

    const handleButtonClick = async (action: string) => {
        if (!state.sessionId) return;

        // Handle feature toggle locally
        if (action.startsWith('feature_') && state.stage === 'SELECT_FEATURES') {
            setState(prev => {
                const isSelected = prev.selectedFeatures.includes(action);
                const newFeatures = isSelected
                    ? prev.selectedFeatures.filter(f => f !== action)
                    : [...prev.selectedFeatures, action];

                // Update button states
                const newButtons = prev.buttons.map(btn =>
                    btn.action === action
                        ? { ...btn, selected: !isSelected, text: isSelected ? btn.text.replace('✅ ', '') : `✅ ${btn.text}` }
                        : btn
                );

                return {
                    ...prev,
                    selectedFeatures: newFeatures,
                    buttons: newButtons,
                };
            });
            return;
        }

        // For features_done, send selected features first
        if (action === 'features_done' && state.selectedFeatures.length > 0) {
            setState(prev => ({ ...prev, isLoading: true }));
            try {
                // Send each selected feature
                for (const feature of state.selectedFeatures) {
                    await simpleChatApi.action(state.sessionId!, feature);
                }
            } catch (error) {
                console.error('Failed to send features:', error);
            }
        }

        // Add user action as message (visual feedback)
        const clickedButton = state.buttons.find(btn => btn.action === action);
        if (clickedButton && !action.startsWith('feature_')) {
            const userMessage = {
                id: Date.now().toString(),
                role: 'user' as const,
                content: clickedButton.text,
                timestamp: new Date(),
            };
            setState(prev => ({
                ...prev,
                messages: [...prev.messages, userMessage],
                isLoading: true,
            }));
        } else {
            setState(prev => ({ ...prev, isLoading: true }));
        }

        try {
            const response = await simpleChatApi.action(state.sessionId, action);

            if (response.data.success) {
                updateFromResponse(response.data.data);

                // Reset features on stage change
                if (response.data.data.stage !== 'SELECT_FEATURES') {
                    setState(prev => ({ ...prev, selectedFeatures: [] }));
                }
            }
        } catch (error) {
            console.error('Failed to process action:', error);
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleTextSubmit = async (text: string) => {
        if (!state.sessionId || !text.trim()) return;

        const userMessage = {
            id: Date.now().toString(),
            role: 'user' as const,
            content: text,
            timestamp: new Date(),
        };

        setState(prev => ({
            ...prev,
            messages: [...prev.messages, userMessage],
            isLoading: true,
        }));

        try {
            const response = await simpleChatApi.message(state.sessionId, text);

            if (response.data.success) {
                updateFromResponse(response.data.data);
            } else if (response.data.error) {
                // Validation error - show error message
                const errorMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant' as const,
                    content: response.data.error.message || t('chat.generic_error'),
                    timestamp: new Date(),
                };
                setState(prev => ({
                    ...prev,
                    messages: [...prev.messages, errorMessage],
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            setState(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleClose = () => {
        setState(prev => ({ ...prev, isOpen: false }));
    };

    const handleEndChat = async () => {
        // Clear session
        localStorage.removeItem('simple_chat_session');

        setState({
            sessionId: null,
            isOpen: false,
            stage: 'MAIN_MENU',
            messages: [],
            buttons: [],
            expectingText: false,
            inputPlaceholder: '',
            isLoading: false,
            selectedFeatures: [],
        });
    };

    return (
        <>
            {/* Floating Button */}
            <ChatButton
                onClick={toggleWidget}
                isOpen={state.isOpen}
                hasUnread={false}
            />

            {/* Chat Window */}
            <AnimatePresence>
                {state.isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="fixed z-50 left-3 right-3 sm:right-auto sm:left-4 sm:w-[380px] bottom-[calc(max(1.25rem,env(safe-area-inset-bottom))+4rem+0.5rem)] sm:bottom-24"
                    >
                        {state.stage === 'COMPLETED' ? (
                            <ThankYouScreen
                                onClose={handleClose}
                                onRestart={handleEndChat}
                            />
                        ) : (
                            <SimpleChatWindow
                                messages={state.messages}
                                buttons={state.buttons}
                                expectingText={state.expectingText}
                                inputPlaceholder={state.inputPlaceholder}
                                onButtonClick={handleButtonClick}
                                onTextSubmit={handleTextSubmit}
                                onClose={handleClose}
                                onEndChat={handleEndChat}
                                isLoading={state.isLoading}
                                stage={state.stage}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default SimpleChatWidget;
